/**
 * Approvals.js — everything waiting for a yes or a no, in one place
 * =================================================================
 * Brian, 27 October 2026: "make an actual way for me to approve stuff easily.
 * right now i have to click into a deal and even then its not clear at all"
 * (D-281). Until then a conversion was answered on Notice Review, a broker row
 * on its deal's Activity tab, and a notice sent twice somewhere else again.
 *
 * Broker rows that need nobody post by themselves, so what is here is only
 * what needs a person, newest kind of decision first:
 *
 *   - conversions and other notices: the figures, what Confirm writes, and
 *     the broker sales that post with it; Confirm or Reject;
 *   - notices sent more than once in one thread: what were they; Answer;
 *   - broker rows the posting held: why, in words; Post or Rule out.
 *
 * Every answer is the same call Notice Review and the pop-up make, each with
 * its dry run before the button that commits (§1). After one, the page and the
 * count on its sidebar link read the list again.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import BrokerRowDecision from "../components/BrokerRowDecision";
import { NEEDS_REVIEW_CHANGED, NEEDS_REVIEW_URL } from "../components/needsReview";
import { day } from "../components/format";
import { Card, PageHeader, LoadingSpinner, Button } from "../components/ui";
import { NoticeCard, SentAgain } from "./NoticeReview";
import "./Approvals.css";

const NOTICE_REVIEW_URL = "/securities/notice-review/";

const HOLD_LABEL = {
  shortfall: "Sells shares the book does not hold",
  waits_on_notice: "Waits on a notice",
  on_ledger: "Looks booked already",
  mirror_pair: "A trade and its cancellation?",
  taken_back: "Taken back to correct",
  cannot_post: "Cannot post as it stands",
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function BrokerRow({ item, onDone }) {
  const [done, setDone] = useState(null);
  return (
    <li className="ap-row">
      <div className="ap-row-head">
        <span className="ap-company">{item.company || "Not tied to a deal"}</span>
        <span className="ap-date">{item.date ? day(item.date) : "No date"}</span>
        <span className="ap-tag">{HOLD_LABEL[item.hold] || "Needs a decision"}</span>
      </div>
      <div className="ap-what">{item.what}</div>
      <div className="ap-why">{item.why}</div>
      {done
        ? <strong className="ap-done" role="status">{done}</strong>
        : <BrokerRowDecision item={item} onDone={(key, words) => { setDone(words); onDone(); }} />}
    </li>
  );
}

export default function Approvals() {
  const [review, setReview] = useState(null);
  const [notices, setNotices] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [r, n] = await Promise.allSettled([api.get(NEEDS_REVIEW_URL), api.get(NOTICE_REVIEW_URL)]);
    if (r.status === "fulfilled") setReview(r.value.data);
    else setError("The list of what is waiting could not be read. Refresh to try again.");
    if (n.status === "fulfilled") setNotices(n.value.data);
    else setNotices({ pending: [], sent_again: [], unreadable: true });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // After an answer: this page, the pop-up and the sidebar's count read again.
  const answered = useCallback(() => {
    window.dispatchEvent(new Event(NEEDS_REVIEW_CHANGED));
    load();
  }, [load]);

  const broker = useMemo(() => (review?.items || []).filter((i) => i.kind === "broker_row"), [review]);
  const thenByNotice = useMemo(() => {
    const out = {};
    for (const i of review?.items || []) if (i.notice_id != null && i.then?.length) out[i.notice_id] = i.then;
    return out;
  }, [review]);
  const pending = notices?.pending || [];
  const sentAgain = notices?.sent_again || [];
  const total = pending.length + sentAgain.length + broker.length;

  if (loading && !review) return <LoadingSpinner message="Reading what is waiting…" />;

  const posting = Number(review?.broker_rows_posting) || 0;
  const bank = Number(review?.bank_rows_waiting) || 0;
  const summary = [
    pending.length && plural(pending.length, "notice to confirm or reject", "notices to confirm or reject"),
    sentAgain.length && plural(sentAgain.length, "notice sent more than once", "notices sent more than once"),
    broker.length && plural(broker.length, "broker row needing a decision", "broker rows needing a decision"),
  ].filter(Boolean).join(" · ");

  return (
    <div className="approvals">
      <PageHeader
        title="Approvals"
        subtitle={total ? summary : "Nothing is waiting for you."}
        action={<Button variant="secondary" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</Button>}
      />

      {error && <Card><p className="ap-error">{error}</p></Card>}
      {review?.email?.reads_as && (
        <p className="ap-email" role="status"><strong>The morning email is not reaching you.</strong> {review.email.reads_as}</p>
      )}

      <p className="ap-intro">
        Broker trades, wires, interest and fees post by themselves, at import and every morning
        {posting > 0 && ` (${plural(posting, "row is", "rows are")} waiting for the next run)`}.
        What is below needs a yes or a no from you.
      </p>

      {pending.length > 0 && (
        <section aria-labelledby="ap-notices">
          <h2 id="ap-notices" className="ap-h2">Conversions and other notices ({pending.length})</h2>
          {pending.map((row) => (
            <NoticeCard key={row.notice_id} row={row} onDone={answered} then={thenByNotice[row.notice_id]} />
          ))}
        </section>
      )}
      {notices?.unreadable && (
        <Card><p className="ap-error">The notices waiting for review could not be read here. <Link to="/notice-review">Open Notice Review</Link>.</p></Card>
      )}

      {sentAgain.length > 0 && (
        <section aria-labelledby="ap-again">
          <h2 id="ap-again" className="ap-h2">Notices sent more than once ({sentAgain.length})</h2>
          <SentAgain rows={sentAgain} onDone={answered} />
        </section>
      )}

      {broker.length > 0 && (
        <section aria-labelledby="ap-broker">
          <h2 id="ap-broker" className="ap-h2">Broker rows that need a decision ({broker.length})</h2>
          <Card>
            <p className="ap-help">
              <strong>Post…</strong> books the row as it stands, with your reason written on it.
              <strong> Rule out…</strong> takes it off for good: a cancellation, a re-send, not ours.
              Each shows what it will do before you commit.
            </p>
            <ul className="ap-list">
              {broker.map((item) => <BrokerRow key={item.key} item={item} onDone={answered} />)}
            </ul>
          </Card>
        </section>
      )}

      {!total && !error && (
        <Card><p className="ap-empty">Nothing is waiting for you. New notices and anything the posting cannot settle will appear here.</p></Card>
      )}

      {bank > 0 && (
        <p className="ap-bank">
          {plural(bank, "bank row is", "bank rows are")} waiting to post. <Link to="/bank-feed">Open Bank Feed</Link>.
        </p>
      )}
    </div>
  );
}
